import type { ObjectId } from 'mongodb';

import type { MonthlyPaymentCap } from './AccountTypes';
import { ProgramType } from './ProgramTypes';

import type { FinancingDecision, UnderwritingNotes } from './index';

export const strictCreditCheckProgramTypes: ProgramType[] = [ProgramType.consumerSolar];

export enum CreditProvider {
  TransUnion = 'transunion',
  Equifax = 'equifax',
  Experian = 'experian',
  TransUnionDirect = 'transunionDirect',
}

/**
 * Per-bureau assessment of the submitted SSN against the SSN on that bureau's file.
 * Bureaus disagree with each other in production, so the verdict is kept per-provider
 * rather than collapsed to a single boolean.
 */
export enum SsnVerdict {
  /** The bureau's file SSN matches what was submitted. */
  confirmed = 'confirmed',
  /** The bureau's file SSN differs from what was submitted. */
  contradicted = 'contradicted',
  /** The bureau performed no usable comparison. */
  noSignal = 'noSignal',
}

export interface CreditCheckIndicativeName {
  firstName: string;
  middleName?: string;
  lastName: string;
  /** Absent on the primary credit-report name entry; set to e.g. "alsoKnownAs" for AKA entries. */
  qualifier?: string;
}

export interface CreditCheckFormattedResponse {
  wasApplicantFound: boolean;
  hasRecentBankruptcy: boolean;
  isCreditFrozen: boolean;
  isFicoQualified: boolean;
  isInvalidFico: boolean;
  noRecentCreditActivity: boolean;
  ficoScore?: number;
  ficoFactors: FicoFactors;
  applicantId?: ObjectId;
  bestQualifiedApplicant?: boolean;
  isCreditApproved?: boolean;
  hasPassingScore?: boolean;
  creditProviders?: CreditProvider[];
  /** Per-bureau scores captured during the waterfall (FIN-4635). Encrypted once persisted. */
  bureauScores?: BureauScore[];
  creditDOB?: string;
  ofacSearchStatus?: string;
  hasCreditFraudAlert?: boolean;
  creditFraudAlertText?: string;
  /** Set per bureau pull. Absent on the condensed per-applicant result. */
  ssnVerdict?: SsnVerdict;
  hasSsnMismatch?: boolean;
  indicativeNames?: CreditCheckIndicativeName[];
}

export interface BureauScore {
  provider: CreditProvider;
  /**
   * FICO score from this bureau. In-memory it is a number; once persisted on the applicant it is
   * a KMS-encrypted string (mirroring how creditCheckResults.ficoScore is stored at rest).
   */
  ficoScore?: number | string;
}

export interface FicoFactors {
  provider: CreditProvider;
  factors: FicoFactor[];
}

export interface FicoFactor {
  rank: string;
  code: string;
}

export interface CreditApprovedEmailData {
  customerAccountLink: string;
  customerName: string;
  customerAddress: string;
  epcName: string;
}

export interface CreditCheckResultUpdate {
  status?: FinancingDecision;
  underwritingNotes?: UnderwritingNotes[];
  encryptedFicoScore?: string;
  creditExpiryDate?: Date;
  monthlyPaymentCap?: MonthlyPaymentCap;
}
